import React, {
    useState,
    ReactNode,
    useEffect,
    useRef,
    Children,
    useTransition,
    useCallback,
    useId,
    ComponentPropsWithRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import DOMPurify from 'dompurify';
import ReactDOM from 'react-dom';
import FMToolTipSprite from '../../../assets/fm-tooltip-icons/fm-tooltip-sprite.svg';
import './fm-tooltip.scss';
import { StringUtil } from '../../utils/string-util/string-util';

export type FMTooltipPositions = 'top' | 'right' | 'bottom' | 'left';
export type FMTooltipAlignment = 'start' | 'center' | 'end';

export interface FMTooltipProps {
    tooltipSymbol?: ReactNode;
    showTooltipAriaLabel?: string;
    dismissTooltipAriaLabel?: string;
    children?: React.ReactElement;
    align?: FMTooltipAlignment;
    side?: FMTooltipPositions;
    tooltipContent:
        | React.ReactNode
        | ((props: {
              fmToolTipCloseButton: React.ReactElement;
          }) => React.ReactElement);
    tooltipVariant?: 'tooltip' | 'popuptip';
    tooltipClosableType?: 'dismissible' | 'persistent';
    gap?: number;
    popuptipFullScreenOnMobile?: boolean;
    popuptipFullScreenMobileBreakpoint?: number;
    tooltipContainerClassName?: string;
    tooltipButtonClassName?: string;
    tooltipPopoverClassName?: string;
    tooltipContentClassName?: string;
    tooltipPopoverStyles?: React.CSSProperties;
    tooltipContentStyles?: React.CSSProperties;
    tooltipButtonStyles?: React.CSSProperties;
    preventHandleOutSideClick?: boolean;
    disableTrigger?: boolean;
    defaultOpen?: boolean;
    open?: boolean;
    onOpen?: (data: boolean) => void;
    onClose?: (data: boolean) => void;
}

export interface FMTooltipHandles {
    triggerRef: HTMLElement | null;
    popoverContentRef: HTMLDivElement | null;
    updatePosition: () => void;
}

export interface CalculatePositionProps {
    side: FMTooltipPositions;
    align: FMTooltipAlignment;
    triggerRef: React.RefObject<HTMLElement>;
    popoverContentRef: React.RefObject<HTMLDivElement>;
    gap?: number;
}

export interface FMToolTipCloseButtonProps
    extends ComponentPropsWithRef<'button'> {
    tooltipClosableType: 'dismissible' | 'persistent';
    tooltipVariant: 'tooltip' | 'popuptip';
    popuptipFullScreenOnMobile: boolean;
}

export interface Position {
    top: string;
    left: string;
}

export interface SetFallbackPositionParams {
    triggerRect: DOMRect;
    popoverRect: DOMRect;
    scrollX: number;
    scrollY: number;
    viewportWidth: number;
    gap: number;
}

export interface FindSuitablePositionParams {
    possibleSides: FMTooltipPositions[];
    possibleAlignments: FMTooltipAlignment[];
    triggerRef: React.RefObject<HTMLElement>;
    popoverContentRef: React.RefObject<HTMLDivElement>;
    gap: number;
}
function callAllFn(...fns: any[]) {
    return (...args: any[]) => {
        fns.forEach(fn => fn && fn(...args));
    };
}

function mergeRefs<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
    return (node: T) => {
        refs.forEach(ref => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && typeof ref === 'object') {
                (ref as React.MutableRefObject<T | null>).current = node;
            }
        });
    };
}

function getHtmlOrChildrenProp(element: React.ReactNode, defaultElement = '') {
    if (element && StringUtil.isHTMLString(element.toString())) {
        return {
            dangerouslySetInnerHTML: {
                __html: DOMPurify.sanitize(element.toString()),
            },
        };
    } else {
        return {
            children: element || defaultElement,
        };
    }
}

function getPreferredSides(
    preferredSide: FMTooltipPositions
): FMTooltipPositions[] {
    const sides: FMTooltipPositions[] = ['top', 'right', 'bottom', 'left'];
    const index = sides.indexOf(preferredSide);
    if (index > -1) {
        sides.splice(index, 1);
        sides.unshift(preferredSide);
    }
    return sides;
}

function getPreferredAlignments(
    preferredAlign: FMTooltipAlignment
): FMTooltipAlignment[] {
    const aligns: FMTooltipAlignment[] = ['start', 'center', 'end'];
    const index = aligns.indexOf(preferredAlign);
    if (index > -1) {
        aligns.splice(index, 1);
        aligns.unshift(preferredAlign);
    }
    return aligns;
}

const getFMToolTipRoot = () => {
    let tooltipRoot = document.getElementById('fm-tooltip-root');
    if (!tooltipRoot) {
        tooltipRoot = document.createElement('div');
        tooltipRoot.setAttribute('id', 'fm-tooltip-root');
        tooltipRoot.setAttribute('class', 'fm-tooltip-root');
        const root = document.getElementById('root');
        if (root) {
            root.appendChild(tooltipRoot);
        } else {
            const body = document.getElementsByTagName('body')[0];
            body.appendChild(tooltipRoot);
        }
    }
    return tooltipRoot;
};

const calculatePosition = ({
    side,
    align,
    triggerRef,
    popoverContentRef,
    gap = 8,
}: CalculatePositionProps) => {
    const triggerRect = triggerRef.current!.getBoundingClientRect();
    const popoverRect = popoverContentRef.current!.getBoundingClientRect();
    const viewportWidth = globalThis.innerWidth;
    const viewportHeight = globalThis.innerHeight;
    const scrollX = globalThis.scrollX || document.documentElement.scrollLeft;
    const scrollY = globalThis.scrollY || document.documentElement.scrollTop;
    let top = 0;
    let left = 0;

    switch (side) {
        case 'top':
            top = triggerRect.top - popoverRect.height - gap;
            break;
        case 'bottom':
            top = triggerRect.bottom + gap;
            break;
        case 'left':
            left = triggerRect.left - popoverRect.width - gap;
            break;
        case 'right':
            left = triggerRect.right + gap;
            break;
    }
    if (side === 'top' || side === 'bottom') {
        switch (align) {
            case 'start':
                left = triggerRect.left;
                break;
            case 'center':
                left =
                    triggerRect.left +
                    (triggerRect.width - popoverRect.width) / 2;
                break;
            case 'end':
                left = triggerRect.right - popoverRect.width;
                break;
        }
    } else {
        switch (align) {
            case 'start':
                top = triggerRect.top;
                break;
            case 'center':
                top =
                    triggerRect.top +
                    (triggerRect.height - popoverRect.height) / 2;
                break;
            case 'end':
                top = triggerRect.bottom - popoverRect.height;
                break;
        }
    }

    top += scrollY;
    left += scrollX;

    const fitsVertically =
        top >= scrollY && top + popoverRect.height <= viewportHeight + scrollY;
    const fitsHorizontally =
        left >= scrollX && left + popoverRect.width <= viewportWidth + scrollX;

    if (fitsVertically && fitsHorizontally) {
        return { top, left };
    }
    return null;
};

function FMToolTipCloseButton({
    tooltipClosableType,
    tooltipVariant,
    'aria-label': dismissTooltipAriaLabel,
    onClick,
    popuptipFullScreenOnMobile,
}: FMToolTipCloseButtonProps) {
    if (tooltipClosableType === 'persistent' && !popuptipFullScreenOnMobile) {
        return null;
    }
    return (
        <button
            onClick={onClick}
            type="button"
            aria-label={dismissTooltipAriaLabel}
            className={`tooltip__close-button ${tooltipVariant}`}
        >
            <svg className={`tooltip__close-icon ${tooltipVariant}`}>
                <use
                    xlinkHref={`${FMToolTipSprite}#${tooltipVariant}-close-icon`}
                />
            </svg>
        </button>
    );
}

const FMTooltip = forwardRef<FMTooltipHandles, FMTooltipProps>(
    function FMTooltip(
        {
            children,
            showTooltipAriaLabel = 'Open for more info',
            tooltipSymbol,
            gap = 8,
            tooltipContent,
            dismissTooltipAriaLabel = 'close',
            tooltipVariant = 'tooltip',
            tooltipClosableType = 'dismissible',
            align = 'start',
            side = 'right',
            tooltipContainerClassName = '',
            tooltipButtonClassName = '',
            defaultOpen = false,
            tooltipPopoverStyles = {},
            tooltipContentStyles = {},
            tooltipButtonStyles = {},
            open,
            onClose,
            onOpen,
            disableTrigger = false,
            preventHandleOutSideClick = false,
            tooltipPopoverClassName = '',
            tooltipContentClassName = '',
            popuptipFullScreenOnMobile = tooltipVariant === 'tooltip'
                ? false
                : true,
            popuptipFullScreenMobileBreakpoint = 500,
        },
        ref
    ) {
        const [isTooltipOpen, setTooltipOpen] = useState<boolean>(
            open ?? defaultOpen
        );
        const child = children ? Children.only(children) : null;
        const triggerRef = useRef<HTMLElement | null>(null);
        const popoverContentRef = useRef<HTMLDivElement | null>(null);
        const [styles, setStyles] = useState<Position>({
            top: '-9999px',
            left: '-9999px',
        });
        const [stylesSet, setStylesSet] = useState(false);
        const [isFullScreen, setIsFullScreen] = useState(false);
        const previousFocusedElementRef = useRef<Element | null>(null);
        const [isPending, startTransition] = useTransition();
        const tooltipContentId = useId();
        const toggleToolTip = () => {
            setTooltipOpen(!isTooltipOpen);
        };

        useEffect(() => {
            if (typeof open === 'boolean') {
                setTooltipOpen(open);
            }
        }, [open]);

        useEffect(() => {
            isTooltipOpen ? onOpen?.(isTooltipOpen) : onClose?.(isTooltipOpen);
        }, [isTooltipOpen]);

        const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
            if (
                !isTooltipOpen &&
                (event.key === 'Enter' || event.key === ' ')
            ) {
                event.preventDefault();
                setTooltipOpen(true);
            } else if (isTooltipOpen && event.key === 'Escape') {
                event.stopPropagation();
                setTooltipOpen(false);
            }
        };

        const handleDocumentKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isTooltipOpen) {
                event.stopPropagation();
                setTooltipOpen(false);
            }
        };

        const handleClickOutside = (event: MouseEvent) => {
            if (preventHandleOutSideClick) {
                return;
            } else if (
                popoverContentRef.current &&
                !popoverContentRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setTooltipOpen(false);
            }
        };

        const trapFocus = (event: KeyboardEvent) => {
            if (isTooltipOpen && popoverContentRef.current) {
                const focusableElements = popoverContentRef.current.querySelectorAll<
                    HTMLElement
                >(
                    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
                );
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement =
                    focusableElements[focusableElements.length - 1];

                if (event.key !== 'Tab') return;
                if (event.shiftKey) {
                    if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        const lockScroll = useCallback(() => {
            document.getElementsByTagName('body')[0].style.overflow = 'hidden';
        }, []);

        const unlockScroll = useCallback(() => {
            document.getElementsByTagName('body')[0].style.overflow = '';
        }, []);
        const updatePosition = useCallback((): void => {
            if (!popoverContentRef.current || !triggerRef.current) {
                return;
            }
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const popoverRect = popoverContentRef.current.getBoundingClientRect();
            const viewportWidth = globalThis.innerWidth;
            const scrollX =
                globalThis.scrollX || document.documentElement.scrollLeft;
            const scrollY =
                globalThis.scrollY || document.documentElement.scrollTop;

            const isFullScreenMode =
                viewportWidth <= popuptipFullScreenMobileBreakpoint &&
                tooltipVariant === 'popuptip' &&
                popuptipFullScreenOnMobile;

            if (isFullScreenMode) {
                handleFullScreen();
                return;
            }

            handleNormalScreen();

            const position = findSuitablePosition({
                possibleSides: getPreferredSides(side),
                possibleAlignments: getPreferredAlignments(align),
                triggerRef,
                popoverContentRef,
                gap,
            });

            if (position) {
                setPopoverPosition(position);
            } else {
                setFallbackPosition({
                    triggerRect,
                    popoverRect,
                    scrollX,
                    scrollY,
                    viewportWidth,
                    gap,
                });
            }
        }, [
            align,
            side,
            gap,
            popuptipFullScreenMobileBreakpoint,
            tooltipVariant,
            popuptipFullScreenOnMobile,
            lockScroll,
            unlockScroll,
            isTooltipOpen,
            isFullScreen,
        ]);

        function handleFullScreen(): void {
            startTransition(() => {
                setIsFullScreen(true);
                setStyles({ top: '0px', left: '0px' });
                setStylesSet(true);
            });
            lockScroll();
        }

        function handleNormalScreen(): void {
            startTransition(() => {
                setIsFullScreen(false);
            });
            unlockScroll();
        }

        function findSuitablePosition({
            possibleSides,
            possibleAlignments,
            triggerRef,
            popoverContentRef,
            gap,
        }: FindSuitablePositionParams): { top: number; left: number } | null {
            for (const side of possibleSides) {
                for (const align of possibleAlignments) {
                    const position = calculatePosition({
                        side,
                        align,
                        triggerRef,
                        popoverContentRef,
                        gap,
                    });
                    if (position) {
                        return position;
                    }
                }
            }
            return null;
        }

        function setPopoverPosition(position: {
            top: number;
            left: number;
        }): void {
            startTransition(() => {
                setStyles({
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                });
                setStylesSet(true);
            });
        }

        function setFallbackPosition({
            triggerRect,
            popoverRect,
            scrollX,
            scrollY,
            viewportWidth,
            gap,
        }: SetFallbackPositionParams): void {
            const position = calculatePosition({
                side: 'bottom',
                align: 'center',
                triggerRef,
                popoverContentRef,
                gap,
            });

            const top = position
                ? position.top
                : triggerRect.bottom + gap + scrollY;
            let left = position
                ? position.left
                : triggerRect.left +
                  (triggerRect.width - popoverRect.width) / 2 +
                  scrollX;
            if (left + popoverRect.width > viewportWidth + scrollX) {
                left = scrollX;
            }
            if (left < scrollX) {
                left = scrollX;
            }

            startTransition(() => {
                setStyles({
                    top: `${top}px`,
                    left: `${left}px`,
                });
                setStylesSet(true);
            });
        }

        useImperativeHandle(
            ref,
            () => ({
                triggerRef: triggerRef.current,
                popoverContentRef: popoverContentRef.current,
                updatePosition,
            }),
            [triggerRef.current, popoverContentRef.current, updatePosition]
        );

        useEffect(() => {
            if (isTooltipOpen) {
                previousFocusedElementRef.current = document.activeElement;
                updatePosition();
                document.addEventListener('keydown', handleDocumentKeyDown);
                document.addEventListener('keydown', trapFocus);
                document.addEventListener('click', handleClickOutside, true);
                globalThis.addEventListener('resize', updatePosition, {
                    passive: true,
                });
                globalThis.addEventListener('scroll', updatePosition, {
                    passive: true,
                });
            } else {
                unlockScroll();
                document.removeEventListener('keydown', handleDocumentKeyDown);
                document.removeEventListener('keydown', trapFocus);
                if (previousFocusedElementRef.current instanceof HTMLElement) {
                    previousFocusedElementRef.current.focus();
                }
                setStylesSet(false);
            }
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
                globalThis.removeEventListener('resize', updatePosition);
                globalThis.removeEventListener('scroll', updatePosition);
                document.removeEventListener('keydown', handleDocumentKeyDown);
                document.removeEventListener('keydown', trapFocus);
            };
        }, [
            isTooltipOpen,
            isFullScreen,
            updatePosition,
            unlockScroll,
            align,
            side,
        ]);
        useEffect(() => {
            if (isTooltipOpen && stylesSet && popoverContentRef.current) {
                popoverContentRef.current.focus({ preventScroll: true });
            }
        }, [isTooltipOpen, stylesSet]);
        const renderTarget = () => {
            if (child) {
                return React.cloneElement(child, {
                    onClick: callAllFn(child.props.onClick, toggleToolTip),
                    onKeyDown: callAllFn(
                        child.props.onKeyDown,
                        handleTriggerKeyDown
                    ),
                    ref: mergeRefs((child as any)?.ref, triggerRef),
                    tabIndex: child.props.tabIndex ?? 0,
                    'aria-haspopup': 'true',
                    'aria-expanded': isTooltipOpen,
                    'aria-describedby': isTooltipOpen
                        ? `tooltip-content-${tooltipContentId}`
                        : undefined,
                });
            }
            return (
                <button
                    ref={triggerRef as React.Ref<HTMLButtonElement>}
                    disabled={disableTrigger}
                    type="button"
                    className={`tooltip__button ${tooltipButtonClassName}`}
                    style={tooltipButtonStyles}
                    aria-label={showTooltipAriaLabel}
                    onClick={toggleToolTip}
                    onKeyDown={handleTriggerKeyDown}
                    aria-haspopup="true"
                    aria-expanded={isTooltipOpen}
                    aria-describedby={
                        isTooltipOpen
                            ? `tooltip-content-${tooltipContentId}`
                            : undefined
                    }
                >
                    {tooltipSymbol ? (
                        tooltipSymbol
                    ) : (
                        <svg style={{ width: '24px', height: '24px' }}>
                            <use xlinkHref={`${FMToolTipSprite}#info-icon`} />
                        </svg>
                    )}
                </button>
            );
        };

        const renderTooltipContent = () => {
            const role = isFullScreen ? 'dialog' : 'tooltip';
            const ariaModal = isFullScreen ? { 'aria-modal': true } : {};
            const fmToolTipCloseButton = (
                <FMToolTipCloseButton
                    onClick={toggleToolTip}
                    aria-label={dismissTooltipAriaLabel}
                    tooltipVariant={tooltipVariant}
                    popuptipFullScreenOnMobile={popuptipFullScreenOnMobile}
                    tooltipClosableType={tooltipClosableType}
                />
            );
            return (
                <div
                    id={`tooltip-content-${tooltipContentId}`}
                    className={`tooltip__content ${tooltipVariant} ${tooltipContentClassName}`}
                    style={tooltipContentStyles}
                    ref={popoverContentRef}
                    role={role}
                    tabIndex={-1}
                    {...ariaModal}
                    aria-hidden={!isTooltipOpen}
                >
                    {typeof tooltipContent === 'function' ? (
                        tooltipContent({ fmToolTipCloseButton })
                    ) : (
                        <>
                            <div
                                tabIndex={0}
                                className="tooltip-text__content"
                                {...getHtmlOrChildrenProp(tooltipContent)}
                            />
                            {fmToolTipCloseButton}
                        </>
                    )}
                </div>
            );
        };

        return (
            <span
                className={`fm-tooltip__container ${tooltipContainerClassName}`}
            >
                {renderTarget()}
                {isTooltipOpen &&
                    tooltipContent &&
                    ReactDOM.createPortal(
                        <div
                            className={`tooltip__popover ${tooltipVariant} ${tooltipPopoverClassName} ${
                                isFullScreen ? 'fullscreen' : ''
                            } ${isPending ? 'transition-pending' : ''}`}
                            style={{
                                ...styles,
                                ...tooltipPopoverStyles,
                                opacity: stylesSet ? 1 : 0,
                            }}
                        >
                            {renderTooltipContent()}
                        </div>,
                        getFMToolTipRoot()
                    )}
            </span>
        );
    }
);

FMTooltip.displayName = 'FMTooltip';
export default FMTooltip;
